import { useContext, useEffect, useState } from 'react';
import MapPinFill from 'remixicon-react/MapPinFillIcon';
import { ProductThemeContext } from '@prishapolicy/shared/ui';
import styles from './vertical-carousel.module.scss';

interface VerticalCarouselCardProps {
  idx: number;
  carouselItem: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    icon: any;
    heading: string;
    text: string;
    url?: string;
    rightContent?: JSX.Element;
    recommendationText?: string;
  };
  active: boolean;
  setCardIdx: React.Dispatch<React.SetStateAction<number>>;
  mapPin?: boolean;
}

const VerticalCarouselCard = (props: VerticalCarouselCardProps) => {
  const Icon = props.carouselItem.icon;
  const productTheme = useContext(ProductThemeContext);

  return (
    <div
      className={`first:rounded-t-large last:rounded-b-large relative  cursor-pointer border pt-8 ${
        !props.active
          ? `border-grey-100 bg-white`
          : `bg-${productTheme?.secondaryShade}  border-${productTheme?.primaryShade}`
      }`}
      onClick={() => {
        if (!props.active) props.setCardIdx(props.idx);
      }}
    >
      <div className="px-5 pb-8">
        <div className="mr-4 grid grid-cols-6 items-center lg:flex">
          <Icon className="text-primary col-span-1 lg:mr-4" />
          <h3 className="lg:heading-5 heading-6 col-span-5">{props.carouselItem.heading}</h3>
        </div>
        <div className="ml-10" style={{ display: props.active ? 'block' : 'none' }}>
          <div>
            <div className="my-2">{props.carouselItem.text}</div>
            {props.mapPin && (
              <div className="text-primary flex w-fit items-end gap-x-2 font-medium">
                <span>
                  <MapPinFill />
                </span>
                {props.carouselItem.url && (
                  <a
                    href={props.carouselItem.url}
                    target="_blank"
                    rel="noreferrer"
                    className="border-primary w-fit border-b"
                  >
                    Open in Google Maps
                  </a>
                )}
              </div>
            )}
            <div className="flex w-fit items-end gap-x-2 font-medium text-red-800">
              <p>{props.carouselItem.recommendationText}</p>
            </div>
          </div>
        </div>
      </div>
      <div className={`z-1000 absolute bottom-0 h-1 w-full  rounded-b-md ${props.active ? 'bg-grey-200' : ''}`}>
        <div
          className={`${styles['progress']} h-1 bg-${productTheme?.primaryShade} ${
            props.active ? styles['active'] : ''
          }`}
        ></div>
      </div>
    </div>
  );
};

export interface VerticalCarouselProps {
  carouselItems: VerticalCarouselCardProps['carouselItem'][];
  mapPin?: boolean;
}

export const VerticalCarousel = (props: {
  carouselItems: VerticalCarouselCardProps['carouselItem'][];
  mapPin?: boolean;
}) => {
  const [cardIdx, setCardIdx] = useState(1);
  // slight trick to make carousel work is set default index to 1, and then set it to 0
  // in use effect, to get the animation going, the catch here is to then always have at least two
  // items in the vertical carousel
  useEffect(() => {
    setCardIdx(0);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCardIdx((cardIdx) => {
        return (cardIdx + 1) % props.carouselItems.length;
      });
    }, 18 * 1000);
    return () => {
      clearInterval(interval);
    };
  }, [props.carouselItems.length]);

  const currentCard = props.carouselItems[cardIdx];
  const RightComponent = currentCard?.rightContent || null;
  return (
    <div className={`lg:grid  grid-cols-${1 + (RightComponent ? 1 : 0)} gap-x-14`}>
      <div className={`rounded-huge grid `}>
        {props.carouselItems.map((carouselItem, idx) => {
          return (
            <VerticalCarouselCard
              active={[-1, idx].includes(cardIdx)}
              carouselItem={carouselItem}
              idx={idx}
              key={idx}
              setCardIdx={setCardIdx}
              mapPin={props.mapPin}
            />
          );
        })}
      </div>
      <div className="mt-8 grid place-items-center lg:mt-0">{RightComponent}</div>
    </div>
  );
};

export function VerticalCardsView(props: VerticalCarouselProps) {
  return (
    <div className="py-8 lg:py-20">
      <div className="container">
        <VerticalCarousel carouselItems={props.carouselItems} />
      </div>
    </div>
  );
}

export default VerticalCarousel;
